import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutTemplate, ArrowRight, SkipForward, Loader2 } from 'lucide-react';
import IvisoraLogo from '../../components/shared/IvisoraLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CreatorOnboardingFirstTemplate = () => {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const handleCreate = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      const token = localStorage.getItem('ivisora_auth_token');
      const res = await fetch(`${API_URL}/api/creator/templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to create template');
      }

      const template = await res.json();
      navigate(`/editor/${template.id}?creatorMode=1`);
    } catch (err) {
      setCreateError(err.message || 'Something went wrong. Please try again.');
      setCreating(false);
    }
  };

  const handleSkip = () => {
    navigate('/creator/dashboard');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12">
      {/* Logo */}
      <div className="mb-8">
        <IvisoraLogo className="h-8 mx-auto" />
      </div>

      {/* Progress */}
      <div className="w-full max-w-md mb-8">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center justify-center">✓</div>
            <span className="text-sm text-gray-400 line-through">Creator Profile</span>
          </div>
          <div className="flex-1 h-px bg-gray-200" />
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-violet-600 text-white text-xs font-bold flex items-center justify-center">2</div>
            <span className="text-sm font-semibold text-violet-600">First Template</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 w-full max-w-md p-8 text-center">
        <div className="w-14 h-14 rounded-2xl bg-violet-50 flex items-center justify-center mx-auto mb-6">
          <LayoutTemplate className="w-7 h-7 text-violet-600" />
        </div>
        <h1 className="text-xl font-bold text-gray-900">Create your first template</h1>
        <p className="text-sm text-gray-500 mt-2 leading-relaxed">
          Use the IVisora editor to design your template. When you're ready, save it and submit it from your Creator Dashboard for review.
        </p>

        <div className="mt-3 bg-violet-50 rounded-xl p-4 text-left space-y-2">
          {[
            'Design your template in the full editor',
            'Save it — it appears in your Drafts',
            'Submit for review from the Templates tab',
            'Once approved, it goes live on the marketplace',
          ].map((step, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-violet-700">
              <span className="w-4 h-4 rounded-full bg-violet-200 text-violet-700 font-bold text-[10px] flex items-center justify-center flex-shrink-0 mt-0.5">
                {i + 1}
              </span>
              {step}
            </div>
          ))}
        </div>

        {createError && (
          <p className="mt-4 text-sm text-red-500 text-center">{createError}</p>
        )}

        <div className="mt-7 space-y-3">
          <button
            onClick={handleCreate}
            disabled={creating}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white font-semibold hover:opacity-90 transition-opacity shadow-lg shadow-violet-500/20 disabled:opacity-60"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {creating ? 'Creating…' : 'Open editor'}
          </button>
          <button
            onClick={handleSkip}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            <SkipForward className="w-4 h-4" /> Skip for now, go to dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreatorOnboardingFirstTemplate;
